import { findLast } from 'lodash-es';
import { catchError, firstValueFrom, ReplaySubject, tap, throwError, timeout } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { QUOTE, QUOTE_REQUEST } from '../../../../../tokens';
import { type CustomerQuote, type QuoteRequest, QuoteStatusEnum, SideEnum } from '../../../../../types';
import { format, toBigWithDefault } from '../../../../../utils';
import type { buildWLAppStateListener } from '../../../WLOrderFormStore';
import {
  resetSwapForm,
  setSwapFromQuantity,
  setSwapQuote,
  setSwapQuoteCountdown,
  setSwapQuoteRequest,
  setSwapStep,
  setSwapToQuantity,
} from '../reducer';

import { isSetSwapRequestSwapAction } from '../matchers';
import {
  selectCurrenciesIncrement,
  selectLastTouchedFields,
  selectLastTouchedOppositeFields,
  selectSecurityAndSide,
} from '../selector';

const DEBOUNCE_MS = 500;

export function setupSwapSliceListeners(startListening: ReturnType<typeof buildWLAppStateListener>) {
  const unsub = startListening({
    predicate: (action, next, prev) => {
      // we only care about actions that change the swap state
      if (next.swap.mode !== 'swap') {
        return false;
      }
      const nextTouchedFields = selectLastTouchedFields(next);
      const prevTouchedFields = selectLastTouchedFields(prev);
      const nextOppositeFields = selectLastTouchedOppositeFields(next);
      const prevOppositeFields = selectLastTouchedOppositeFields(prev);

      // if explicitly requesting a swap, we trigger the effect to get the latest quote
      if (isSetSwapRequestSwapAction(action) || resetSwapForm.match(action)) {
        return true;
      }

      const hasMarketAccountChanged =
        next.swap.form.marketAccount.value?.SourceAccountID !== prev.swap.form.marketAccount.value?.SourceAccountID;

      const hasTouchedFieldQuantityChanged =
        nextTouchedFields.quantityField.value !== prevTouchedFields.quantityField.value;
      const hasTouchedFieldCurrencyChanged =
        nextTouchedFields.currencyField.value !== prevTouchedFields.currencyField.value;
      const hasOppositeFieldCurrencyChanged =
        nextOppositeFields.currencyField.value !== prevOppositeFields.currencyField.value;

      return (
        hasTouchedFieldQuantityChanged ||
        hasTouchedFieldCurrencyChanged ||
        hasOppositeFieldCurrencyChanged ||
        hasMarketAccountChanged
      );
    },

    effect: async (action, listenerApi) => {
      // Cancel any ongoing debounce timers or effects
      listenerApi.cancelActiveListeners();
      let state = listenerApi.getState();

      let lastTouchedFields = selectLastTouchedFields(state);
      if (toBigWithDefault(lastTouchedFields.quantityField.value, 0).eq(0)) {
        // the last touched field is zero, we remove the quote, quoterequest, and reset fields
        listenerApi.dispatch(setSwapQuoteRequest(undefined));
        listenerApi.dispatch(setSwapQuote(undefined));
        const oppositeFields = selectLastTouchedOppositeFields(state);
        if (oppositeFields.quantityField.key === 'toQty') {
          listenerApi.dispatch(setSwapToQuantity({ qty: '', setTouched: false }));
        }
        if (oppositeFields.quantityField.key === 'fromQty') {
          listenerApi.dispatch(setSwapFromQuantity({ qty: '', setTouched: false }));
        }
        return;
      }

      // Wait for debounce delay
      await listenerApi.delay(DEBOUNCE_MS);

      state = listenerApi.getState();
      lastTouchedFields = selectLastTouchedFields(state);
      const securityAndSide = selectSecurityAndSide(state);
      const marketAccount = state.swap.form.marketAccount.value?.SourceAccountID;
      if (
        !securityAndSide ||
        !marketAccount ||
        !lastTouchedFields.quantityField.value ||
        !lastTouchedFields.currencyField.value?.Symbol
      ) {
        return;
      }
      const { security, side } = securityAndSide;
      const QuoteReqID = uuid();

      const quoteRequest: QuoteRequest = {
        Symbol: security.Symbol,
        Side: side,
        OrderQty: lastTouchedFields.quantityField.value,
        Currency: lastTouchedFields.currencyField.value.Symbol,
        QuoteReqID,
        MarketAccount: marketAccount,
        tag: 'quote_request',
        TransactTime: new Date().toISOString(),
      };
      const wsClient = listenerApi.extra.wsClient;
      wsClient.registerPublication({
        type: QUOTE_REQUEST,
        data: [quoteRequest],
      });
      listenerApi.dispatch(setSwapQuoteRequest(quoteRequest));

      const quoteSubject = new ReplaySubject<CustomerQuote>(1);

      const address = uuid();
      // subscribe to the quote response
      // TODO(robertz): Consider moving the quote request to store extra, and have it be alive always.
      wsClient.registerSubscription(
        address,
        [
          {
            name: QUOTE,
            tag: 'quote_request',
            Symbol: quoteRequest.Symbol,
            HideApiCalls: true,
            QuoteReqID: quoteRequest.QuoteReqID,
            sort_by: '-Timestamp',
          },
        ],
        (err, json) => {
          if (json?.data?.length) {
            const quote = findLast(
              json.data,
              (d: CustomerQuote) =>
                d.QuoteReqID === quoteRequest.QuoteReqID &&
                (d.QuoteStatus === QuoteStatusEnum.Open || d.QuoteStatus === QuoteStatusEnum.Rejected)
            ) as CustomerQuote | undefined;
            if (quote) {
              quoteSubject.next(quote);
            }
          }
        }
      );

      const quote = await firstValueFrom(
        quoteSubject.pipe(
          timeout(5000), // adjust the timeout duration as needed
          tap(() => wsClient.unregisterSubscription(address)),
          catchError(err => {
            wsClient.unregisterSubscription(address);
            return throwError(() => err);
          })
        )
      ).catch(err => {
        return undefined;
      });

      if (!quote) {
        // TODO(robertz): Determine how to handle the case where the quote is not available within 5sec
        console.error('failed to get quote');
        return;
      }

      // the quote request might have changed, so we need to check if the quote matches the request
      if (listenerApi.getState().swap.quoteRequest?.QuoteReqID !== quote.QuoteReqID) {
        return;
      }

      const lockQuote = isSetSwapRequestSwapAction(action) && action.payload === 'locking';
      listenerApi.dispatch(setSwapQuote({ quote, lock: lockQuote }));

      // given the quote, we need to index into the correct qty and set the corresponding field
      // based on the last touched field
      const fieldToPopulate = selectLastTouchedOppositeFields(state).quantityField;
      const fieldToGrab: keyof Pick<CustomerQuote, 'BidAmt' | 'OfferAmt' | 'BidPx' | 'OfferPx'> =
        side === SideEnum.Buy ? 'OfferAmt' : 'BidAmt';

      const isRejected = quote.QuoteStatus === QuoteStatusEnum.Rejected;

      let qty = !isRejected ? quote[fieldToGrab] : '0';
      if (toBigWithDefault(qty, 0).eq(0)) {
        qty = undefined;
      }

      const { toCurrencyDetails, fromCurrencyDetails } = selectCurrenciesIncrement(listenerApi.getState());

      if (fieldToPopulate.key === 'toQty') {
        qty = format(qty, { truncate: true, spec: toCurrencyDetails?.defaultIncrement, pretty: false });
        listenerApi.dispatch(setSwapToQuantity({ qty: qty, setTouched: false }));
      } else if (fieldToPopulate.key === 'fromQty') {
        qty = format(qty, { truncate: true, spec: fromCurrencyDetails?.defaultIncrement, pretty: false });
        listenerApi.dispatch(setSwapFromQuantity({ qty: qty, setTouched: false }));
      }

      if (lockQuote && quote.ValidUntilTime) {
        const endTime = new Date(quote.ValidUntilTime).getTime();
        let now = Date.now();
        while (now < endTime && listenerApi.getState().swap.meta.step === 'locked') {
          const remaining = Math.ceil((endTime - now) / 1000);
          listenerApi.dispatch(setSwapQuoteCountdown(remaining));
          await listenerApi.delay(1000);
          now = Date.now();
        }
        // if we are still in the locked step after the countdown, we reset the quote and quote request
        // this means that the countdown has finished, and no other action that changed the step has been dispatched
        if (listenerApi.getState().swap.meta.step === 'locked') {
          // after the countdown, we reset the quote and quote request
          listenerApi.dispatch(setSwapQuote({ quote: undefined, lock: false }));
          listenerApi.dispatch(setSwapQuoteRequest(undefined));
          listenerApi.dispatch(setSwapQuoteCountdown(undefined));
          listenerApi.dispatch(setSwapStep('idle'));
        }
      }
    },
  });
  return () => {
    unsub();
  };
}
